import React, { useState, useRef, useCallback } from 'react';
import { Tooltip } from "reactstrap";
import User from './dropdown/user/User';
import OrganisationSelector from "src/components/organisation/selector";
import { useChat } from 'react-live-chat-loader'
import { Bars4Icon } from '@heroicons/react/20/solid';
import { useSelector } from 'react-redux';

import { ChatBubbleLeftRightIcon, MagnifyingGlassIcon, PlusIcon, ChevronDownIcon, ClipboardDocumentIcon, LinkIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useHistory } from 'react-router-dom';
import domain from 'src/config/domain';
import CreateOrganisation from 'src/pages/admin/pages/create-organisation';


const Header = ({ fixed, theme, className, sidebarToggle, ...props }) => {
  const [state, loadChat] = useChat();
  const { organisation: { is_admin } } = useSelector((state) => state);
  const domainConfig = domain();
  const history = useHistory();

  const [tooltipOpen, setOpen] = useState(false);
  const toggle = () => { setOpen(!tooltipOpen) };
  const [createOrgModal, setCreateOrgModal] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const createBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  const toggleCreate = useCallback(() => {
    setCreateOpen((prev) => {
      if (!prev && createBtnRef.current) {
        const rect = createBtnRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
      }
      return !prev;
    });
  }, []);

  const createItems = [
    { label: 'Booking', description: 'Create a new booking for a customer', icon: ClipboardDocumentIcon, action: () => history.push('/bookings/create') },
    { label: 'Payment Link', description: 'Generate a shareable payment link', icon: LinkIcon, action: () => history.push('/payment-link/create') },
    ...(is_admin ? [{ label: 'Organisation', description: 'Create a new organisation', icon: BuildingOfficeIcon, action: () => setCreateOrgModal(true), variant: 'admin' }] : []),
  ];

  return (
    <React.Fragment>
      <header className={`w-auto text-sm py-2 sm:py-4 w-100 pr-4 pl-1 ${domainConfig.styles.lightBorderColour} ${domainConfig.styles.lightBGColour}`}>
        <nav
          className="grid grid-cols-4 gap-4 lg:h-full"
          aria-label="Global"
        >
          <div className="col-span-2">
            <div className='lg:ml-2 float-left'>
              <OrganisationSelector showWithNoDefault={false} />
            </div>
          </div>
          <div className="col-span-2 flex flex-row items-center justify-end gap-2 pt-[19px]">

            {/* Search */}
            <button
              onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
              className="mr-2 hidden lg:inline-flex items-center gap-1.5 py-1.5 pl-2.5 pr-1.5 rounded-md text-xs text-violet-800 bg-white hover:bg-gray-50 border border-violet-300 w-[200px] xl:w-[260px] cursor-text"
              title="Search (Cmd+K)"
            >
              <MagnifyingGlassIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 text-left font-normal">Search for anything...</span>
              <kbd className="text-[10px] font-medium text-white bg-violet-600 px-1 py-0.5 rounded leading-none">&#8984;K</kbd>
            </button>

            {/* Create dropdown */}
            <div className="hidden lg:block pl-2 mr-2">
              <button
                ref={createBtnRef}
                onClick={toggleCreate}
                className={`flex items-center gap-1.5 py-1.5 px-3  rounded-md text-xs font-medium text-white ${domainConfig.styles.primaryColour} hover:${domainConfig.styles.lightBGColour} border ${domainConfig.styles.lightBorderColour} transition-colors`}
              >
                <PlusIcon className="h-3.5 w-3.5 shrink-0" />
                Create
                <ChevronDownIcon className={`h-3 w-3 shrink-0 transition-transform ${createOpen ? 'rotate-180' : ''}`} />
              </button>

              {createOpen && (
                <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => setCreateOpen(false)} />
                  <div
                    className="fixed w-[320px] bg-white rounded-xl shadow-xl ring-1 ring-black/5 border border-gray-200 py-2 z-[9999]"
                    style={{ top: dropdownPos.top, right: dropdownPos.right }}
                  >
                    {createItems.map((item, i) => (
                      <button
                        key={item.label}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${i > 0 ? 'border-t border-gray-100' : ''}`}
                        onClick={() => { setCreateOpen(false); item.action(); }}
                      >
                        <div className={`mt-0.5 p-1.5 mr-2 float-left rounded-md ${item.variant === 'admin' ? 'bg-red-50' : 'bg-violet-50'}`}>
                          <item.icon className={`h-4 w-4 shrink-0 ${item.variant === 'admin' ? 'text-red-500' : domainConfig.styles.textColour}`} />
                        </div>
                        <div className="min-w-0">
                          <div className={`text-sm font-medium ${item.variant === 'admin' ? 'text-red-700' : 'text-violet-900'}`}>{item.label}</div>
                          <div className="text-xs text-black leading-snug mt-0.5">{item.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Help & User */}
            {state !== 'test' && (
              <div className="shrink-0 inline-flex items-center pl-2">
                <div className="user-toggle" onClick={() => loadChat({ open: true })}>
                  <ChatBubbleLeftRightIcon id="question-icon" className={`h-8 w-8 ${domainConfig.styles.textColour} cursor-pointer`} />
                  <Tooltip placement="right" isOpen={tooltipOpen} target="question-icon" toggle={toggle}>
                    Help & Guides
                  </Tooltip>
                </div>
              </div>
            )}

            <div className="shrink-0 h-[2.375rem] inline-flex justify-center items-center gap-x-2">
              <User />
            </div>

            <Bars4Icon
              className="h-6 w-6 ml-1 lg:hidden"
              onClick={sidebarToggle}
              style={{ color: '#6b21a8', cursor: 'pointer' }}
            />

          </div>

        </nav>

        {/* Mobile create buttons */}
        <div className="flex gap-2 w-full lg:hidden mt-4 mx-2 clearfix h-auto">
          <button
            onClick={() => history.push('/bookings/create')}
            className={`flex items-center justify-center gap-1 py-2 px-3 rounded-md text-xs font-medium ${domainConfig.styles.textColour} bg-white hover:${domainConfig.styles.lightBGColour} border ${domainConfig.styles.lightBorderColour} flex-1 transition-colors`}
          >
            <PlusIcon className='h-3.5 w-3.5 shrink-0' /> Booking
          </button>
          <button
            onClick={() => history.push('/payment-link/create')}
            className={`flex items-center justify-center gap-1 py-2 px-3 rounded-md text-xs font-medium ${domainConfig.styles.textColour} bg-white hover:${domainConfig.styles.lightBGColour} border ${domainConfig.styles.lightBorderColour} flex-1 transition-colors`}
          >
            <PlusIcon className='h-3.5 w-3.5 shrink-0' /> Payment Link
          </button>
        </div>


      </header>
      {is_admin === true && (
        <CreateOrganisation isOpen={createOrgModal} toggle={() => setCreateOrgModal(false)} />
      )}
    </React.Fragment>
  );
};
export default Header;
